import {
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { CheckIcon } from 'lucide-react';
import { useRoleApi } from '../../services/api/useRole.api';
import { Role } from '../../types/Role/Role.type.ts';
import { Role as RoleEnum } from '../../types/enums/Role.enum';
import { Permission } from '../../types/Permission.type.ts';

const ROLE_CHECK_COLOR: Record<number, string> = {
  [RoleEnum.MEMBER]: '#a0aec0',
  [RoleEnum.ADMIN]: '#132B50',
  [RoleEnum.SUPER_ADMIN]: '#2f855a',
};

const roleHasPermission = (role: Role, permissionId: number): boolean => {
  let current: Role | null = role;
  while (current) {
    if (current.role_permissions.some((rp) => rp.permission.id === permissionId)) {
      return true;
    }
    current = current.parent_role;
  }
  return false;
};

const collectPermissions = (roles: Role[]): Permission[] => {
  const map = new Map<number, Permission>();
  roles.forEach((role) => {
    let current: Role | null = role;
    while (current) {
      current.role_permissions.forEach((rp) => map.set(rp.permission.id, rp.permission));
      current = current.parent_role;
    }
  });
  return [...map.values()].sort((a, b) => b.id - a.id);
};

function RoleMatrix() {
  const { getRoles } = useRoleApi();

  const { data: roles, isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: getRoles,
  });

  const sortedRoles = [...(roles ?? [])].sort((a, b) => b.id - a.id);
  const permissions = collectPermissions(sortedRoles);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <TableContainer component={Paper} sx={{ border: '1px solid #cbd5e0', boxShadow: 'none' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
              {permissions.map((permission) => (
                <TableCell key={permission.id} align='center' sx={{ fontWeight: 600 }}>
                  {permission.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRoles.map((role) => (
              <TableRow key={role.id} hover>
                <TableCell sx={{ py: 2 }}>
                  <Stack spacing={0.5}>
                    <Typography
                      variant='subtitle1'
                      sx={{
                        fontWeight: 'bold',
                      }}
                    >
                      {role.name}
                    </Typography>

                    <Typography
                      variant='body2'
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {role.description}
                    </Typography>
                  </Stack>
                </TableCell>
                {permissions.map((permission) => {
                  if (!roleHasPermission(role, permission.id)) {
                    return (
                      <TableCell key={permission.id} align='center' sx={{ py: 2 }}>
                        <Typography
                          component='span'
                          sx={{
                            color: 'text.disabled',
                            fontSize: 18,
                          }}
                        >
                          —
                        </Typography>
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell key={permission.id} align='center' sx={{ py: 2 }}>
                      <Tooltip title={permission.description} placement='top'>
                        <Box component='span' sx={{ display: 'inline-flex' }}>
                          <CheckIcon size={24} color={ROLE_CHECK_COLOR[role.id]} />
                        </Box>
                      </Tooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

export default RoleMatrix;
